<script setup lang="ts">
  import type { Component } from 'vue';
  import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue';
  import { onBeforeRouteLeave, useRoute } from 'vue-router';
  import fileTexMultiple from '@/shared/assets/icons/FileTextMultiple.svg';
  import tableIcon from '@/shared/assets/icons/TableIcon.svg';
  import {
    FieldSearchBar,
    SearchToolbar,
    SelectInput,
    NoDocuments,
    PaginateOrScroll,
    TabsMenu,
  } from '@/shared/ui';
  import { DocumentFilterModal } from '@/features/Document';
  import { useApprovalOrVerificationSearch } from '@/features/Search';
  import { useFilterStore } from '@/entities/Filter';
  import { useTableFilterModal } from '@/shared/lib/use';
  import {
    DocumentType,
    documentTypeToTabOptionId,
    isDocumentRegistrationTabOptionId,
    tabOptionIdToDocumentType,
    useDocumentSearchTypeStore,
  } from '@/shared/lib';
  import type { SearchFilter, SearchParams, SortOrder } from '@/shared/lib';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import { APPROVAL_TAB_CONFIG, getApprovalTabConfig } from '../../config';

  const APPROVAL_DEFAULT_SORT = {
    sort: '',
    order: 'desc' as SortOrder,
  };

  const buildDefaultApprovalSearchFilter = (
    documentType: DocumentType = DocumentType.DOCUMENT
  ) => ({
    revisionStatus: '',
    type: documentType,
    date: '',
    revision: '',
    user: '',
    ...(documentType === DocumentType.REGISTRATION ? { elaboratedBy: '' } : {}),
  });

  const isFilterDocumentType = (value: unknown): value is DocumentType =>
    value === DocumentType.DOCUMENT || value === DocumentType.REGISTRATION;

  const TAB_ORDER = [DocumentType.DOCUMENT, DocumentType.REGISTRATION];

  // Os mesmos pictogramas da tela de Verificação: as duas telas irmãs deixam de divergir.
  const TAB_ICONS: Record<DocumentType, string> = {
    [DocumentType.DOCUMENT]: fileTexMultiple,
    [DocumentType.REGISTRATION]: tableIcon,
  };

  const TAB_OPTIONS = TAB_ORDER.map((documentType) => ({
    id: documentTypeToTabOptionId(documentType),
    text: APPROVAL_TAB_CONFIG[documentType].tabLabel,
    icon: TAB_ICONS[documentType],
  }));

  const props = withDefaults(
    defineProps<{
      title?: string;
      tableComponent: Component;
      emptyStateTitle?: string;
    }>(),
    {
      title: 'Etapa de Análise Crítica e Aprovação',
    }
  );

  const route = useRoute();

  const initialDocumentType =
    route.query.type === DocumentType.REGISTRATION
      ? DocumentType.REGISTRATION
      : DocumentType.DOCUMENT;

  const selectedDocumentType = ref<DocumentType>(initialDocumentType);

  const initialSearchParams: SearchParams = {
    search: '',
    sort: APPROVAL_DEFAULT_SORT,
    filter: buildDefaultApprovalSearchFilter(initialDocumentType),
    page: 1,
    amount: 10,
    // Getter, e nao valor: cada aba le a sua fonte, e a troca de aba precisa trocar a URL da busca.
    get searchUrl() {
      return getApprovalTabConfig(selectedDocumentType.value).searchUrl;
    },
    systemFilters: ['type'],
  };

  const {
    items: revisions,
    metadata,
    selectedQuantity,
    allItems: allRevisions,
    hasMoreItems: hasMoreRevisions,
    loadingMore,
    shouldShowTable,
    shouldShowEmptyPage,
    isUsingFilters,
    handleSearch,
    handleFilter: baseHandleFilter,
    handleSortOptions,
    onPageChanged,
    handleQuantityChange,
    applyListQueryReset,
    initSearch,
    cleanup,
    filter,
    searchTerm,
    sort,
    order,
  } = useApprovalOrVerificationSearch(initialSearchParams);

  provide(SortOrderInjectionKey, { sort, order });

  const filterStore = useFilterStore();

  const { setSearchType } = useDocumentSearchTypeStore();

  // A fila de registros declara o elaborador em `elaboratedBy` e descarta `user`; a de documentos
  // segue em `user`. O modal escreve sempre em `user`, entao a traducao vive so nesta tela.
  const isRegistrationTab = () => selectedDocumentType.value === DocumentType.REGISTRATION;

  const handleFilterMerged = async (filters: SearchFilter) => {
    const rest = { ...filters };
    delete rest.type;
    if (isRegistrationTab()) {
      rest.elaboratedBy = rest.user ?? rest.elaboratedBy ?? '';
      rest.user = '';
    }
    await baseHandleFilter({
      ...buildDefaultApprovalSearchFilter(selectedDocumentType.value),
      ...rest,
      type: selectedDocumentType.value,
    });
  };

  const getModalFilters = () => {
    const modalFilters = { ...filter.value };
    delete modalFilters.type;
    if (isRegistrationTab()) {
      modalFilters.user = modalFilters.elaboratedBy ?? '';
      delete modalFilters.elaboratedBy;
    }
    return modalFilters;
  };

  const { openFilterModal } = useTableFilterModal(
    DocumentFilterModal,
    filterStore,
    handleFilterMerged,
    getModalFilters,
    {
      variant: 'approval',
      elaborationPermission: { action: 'send', subject: 'Document' },
      onClearFilters: () => {
        filterStore.clearFilters();
        void handleFilterMerged(buildDefaultApprovalSearchFilter(selectedDocumentType.value));
      },
    }
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  const selectedTabId = computed(() => documentTypeToTabOptionId(selectedDocumentType.value));

  const tabConfig = computed(() => getApprovalTabConfig(selectedDocumentType.value));

  const resolvedEmptyTitle = computed(() => props.emptyStateTitle ?? tabConfig.value.emptyTitle);

  watch(
    [sort, tabConfig],
    ([currentSort, currentTabConfig]) => {
      if (currentSort === '') {
        return;
      }
      const isOfferedByTab = currentTabConfig.orderOptions.some(
        (option) => option.sort === currentSort
      );
      if (!isOfferedByTab) {
        void handleSortOptions('', APPROVAL_DEFAULT_SORT.order);
      }
    },
    { immediate: true }
  );

  const handleTabSelected = async (optionId: string) => {
    if (!isDocumentRegistrationTabOptionId(optionId)) {
      return;
    }

    const nextDocumentType = tabOptionIdToDocumentType(optionId);
    if (nextDocumentType === selectedDocumentType.value) {
      return;
    }
    selectedDocumentType.value = nextDocumentType;
    filterStore.clearFilters();
    setSearchType('prefix');
    await applyListQueryReset({
      search: '',
      sort: APPROVAL_DEFAULT_SORT,
      filter: buildDefaultApprovalSearchFilter(nextDocumentType),
      quantity: initialSearchParams.amount,
    });
  };

  const hasMounted = ref(false);

  // A aba tambem muda pela URL, e nao so pelo clique: voltar no navegador troca `?type=` sem passar
  // por `resetListStateForTabChange`. Como cada aba le uma tabela diferente, so trocar o rotulo
  // deixaria as linhas da aba anterior na tela, sob os titulos da nova. A recarga so acontece
  // depois da montagem, porque `onMounted` ja faz a primeira busca.
  watch(
    () => filter.value.type,
    (filterType) => {
      if (isFilterDocumentType(filterType) && selectedDocumentType.value !== filterType) {
        selectedDocumentType.value = filterType;
        if (hasMounted.value) {
          void initSearch();
        }
      }
    },
    { immediate: true }
  );

  onMounted(async () => {
    await initSearch();
    hasMounted.value = true;
  });

  onUnmounted(() => {
    cleanup();
  });

  onBeforeRouteLeave(() => {
    filterStore.clearFilters();
  });
</script>

<template>
  <div class="pt-4 p-6">
    <h3 class="text-gray-07 text-xl/7.5 font-semibold text-left mb-4">
      {{ props.title }}
    </h3>

    <TabsMenu
      class="mb-4 bg-[var(--color-gray-02)]!"
      :options="TAB_OPTIONS"
      :selected-option="selectedTabId"
      @option-selected="handleTabSelected"
    />

    <SearchToolbar
      v-if="!shouldShowEmptyPage"
      class="mb-6"
    >
      <template #search>
        <FieldSearchBar
          :search-fields="tabConfig.searchFields"
          :initial-search-term="searchTerm"
          class="w-full"
          @search="handleSearch"
        />
      </template>
      <template
        v-if="shouldShowTable"
        #quantity
      >
        <SelectInput
          v-model="selectedQuantity"
          :options="tabConfig.quantityOptions"
          class="cursor-pointer"
          @update:model-value="handleQuantityChange"
        />
      </template>
    </SearchToolbar>

    <PaginateOrScroll
      v-if="shouldShowTable"
      :items="revisions"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      :all-items="allRevisions"
      :loading-more="loadingMore"
      :has-more-items="hasMoreRevisions"
      :entity-name="tabConfig.paginationEntity"
      @page-changed="onPageChanged"
    >
      <template #table="{ items }">
        <component
          :is="props.tableComponent"
          :revisions="items"
          :is-filter-active="isUsingFilters"
          :order-options="tabConfig.orderOptions"
          :code-column-label="tabConfig.codeColumnLabel"
          @update:sort="handleSortOptions"
          @update:filter="handleFilterMerged"
          @filter-clicked="handleFilterClicked"
        />
      </template>
    </PaginateOrScroll>
    <NoDocuments
      v-if="shouldShowEmptyPage"
      :title="resolvedEmptyTitle"
    />
  </div>
</template>
